/**
 * Custom wrapper around React Query's useQuery hook
 * Provides consistent error handling, loading states, and type safety
 */

import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import { APIError } from '@/utils/api-client';

interface UseApiQueryOptions<
  TQueryFnData = unknown,
  TError = APIError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> extends Omit<
  UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  'queryKey' | 'queryFn'
> {
  showErrorToast?: boolean;
  errorMessage?: string;
}

export function useApiQuery<
  TQueryFnData = unknown,
  TError = APIError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  queryFn: () => Promise<TQueryFnData>,
  options?: UseApiQueryOptions<TQueryFnData, TError, TData, TQueryKey>
): UseQueryResult<TData, TError> {
  const {
    showErrorToast = true,
    errorMessage,
    ...queryOptions
  } = options || {};

  // Error toasts are shown globally from QueryCache.onError (see
  // query-client-config.ts), keyed off this meta - not from an effect here,
  // which would opt this query into React Query's tracked-fields
  // optimization and risk missing updates for consumers that never render
  // `data`/`isSuccess` directly.
  return useQuery({
    queryKey,
    queryFn,
    ...queryOptions,
    meta: { showErrorToast, errorMessage, ...queryOptions.meta },
  });
}
